import type { AuthBusiness, AuthUser, BusinessStatus, RegisterPayload, Role } from "../types/auth";

type ApiErrorShape = {
  code: string;
  message?: string;
  data?: unknown;
};

export class ApiError extends Error {
  code: string;
  data?: unknown;
  constructor({ code, message, data }: ApiErrorShape) {
    super(message ?? code);
    this.code = code;
    this.data = data;
  }
}

// Default to local Laravel server; override via VITE_API_BASE_URL if needed.
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://127.0.0.1:8000";

// Deduplicate concurrent identical GET requests (dev remounts, racing effects, etc.).
const inFlightGet = new Map<string, Promise<unknown>>();

async function requestJson<T>(
  path: string,
  opts: { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; token?: string; body?: unknown } = {},
): Promise<T> {
  const { method = "GET", token, body } = opts;

  const doFetch = async (): Promise<T> => {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const raw = await res.text();
    let json: any = null;
    if (raw) {
      try {
        json = JSON.parse(raw);
      } catch {
        json = null;
      }
    }

    if (!res.ok) {
      const tokenFromHeader = res.headers.get("X-Auth-Token") ?? undefined;
      const code = json?.error ?? json?.code ?? `HTTP_${res.status}`;
      const message = json?.message ?? json?.error ?? res.statusText;

      throw new ApiError({
        code,
        message,
        data: {
          // Preserve common Laravel validation + error payloads for the UI.
          errors: json?.errors,
          raw: json,
          token: tokenFromHeader,
          user: json?.user,
          business: json?.business,
          completion: typeof json?.completion === "number" ? json.completion : undefined,
          next_step: typeof json?.next_step === "string" ? json.next_step : undefined,
        },
      });
    }

    return json as T;
  };

  if (method === "GET" && body === undefined) {
    const key = `${method} ${path} ${token ?? ""}`;
    const existing = inFlightGet.get(key) as Promise<T> | undefined;
    if (existing) return existing;
    const p = doFetch().finally(() => inFlightGet.delete(key));
    inFlightGet.set(key, p as unknown as Promise<unknown>);
    return p;
  }

  return doFetch();
}

async function requestFormData<T>(
  path: string,
  opts: { method?: "POST" | "PUT" | "PATCH"; token?: string; formData: FormData },
): Promise<T> {
  const { method = "POST", token, formData } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: formData,
  });

  const raw = await res.text();
  let json: any = null;
  if (raw) {
    try {
      json = JSON.parse(raw);
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    const tokenFromHeader = res.headers.get("X-Auth-Token") ?? undefined;
    const code = json?.error ?? json?.code ?? `HTTP_${res.status}`;
    const message = json?.message ?? json?.error ?? res.statusText;

    throw new ApiError({
      code,
      message,
      data: {
        errors: json?.errors,
        raw: json,
        token: tokenFromHeader,
        user: json?.user,
        business: json?.business,
        completion: typeof json?.completion === "number" ? json.completion : undefined,
        next_step: typeof json?.next_step === "string" ? json.next_step : undefined,
      },
    });
  }

  return json as T;
}

/** Aggregated GET /api/dashboard/business */
export type BusinessDashboardResponse = {
  data: {
    revenue_snapshot: {
      today_sales: string;
      total_orders: number;
      avg_order_value: string;
    };
    revenue_comparison: {
      current_total_revenue: string;
      previous_total_revenue: string;
      current_total_orders: number;
      previous_total_orders: number;
      current_from_date: string;
      current_to_date: string;
      previous_from_date: string;
      previous_to_date: string;
    };
    purchase_orders: {
      pending_total: number;
      pending_current_period: number;
      pending_previous_period: number;
      current_from_date: string;
      current_to_date: string;
      previous_from_date: string;
      previous_to_date: string;
    };
    sales_trend: {
      last_7_days: Array<{ date: string; total_sales: string }>;
      last_30_days: Array<{ date: string; total_sales: string }>;
    };
    top_products: Array<{
      product_id: string;
      product_name: string;
      quantity: string;
      revenue: string;
    }>;
    inventory_summary: {
      total_skus: number;
      total_quantity: string;
      inventory_value: string;
    };
    alerts: {
      low_stock: { count: number; samples: Array<{ product_id: string; product_name: string; quantity_remaining: string }> };
      out_of_stock: { count: number; samples: Array<{ product_id: string; product_name: string; quantity_remaining: string }> };
    };
    recent_activity: Array<{
      type: string;
      reference: string;
      label: string;
      amount: string | null;
      created_at: string | null;
      /** User/cashier name for sales */
      customer?: string | null;
    }>;
    insights: unknown[] | null;
  };
  meta: { generated_at: string; cache_ttl_seconds: number };
};

/** Aggregated GET /api/dashboard/summary */
export type BusinessDashboardSummaryResponse = {
  data: {
    revenue_snapshot: BusinessDashboardResponse["data"]["revenue_snapshot"];
    revenue_comparison: BusinessDashboardResponse["data"]["revenue_comparison"];
    purchase_orders: BusinessDashboardResponse["data"]["purchase_orders"];
    sales_trend: BusinessDashboardResponse["data"]["sales_trend"];
    alerts: BusinessDashboardResponse["data"]["alerts"];
    recent_activity: BusinessDashboardResponse["data"]["recent_activity"];
  };
  meta: { generated_at: string; cache_ttl_seconds: number };
};

/** Aggregated GET /api/dashboard/analytics */
export type BusinessDashboardAnalyticsResponse = {
  data: {
    sales_trend: BusinessDashboardResponse["data"]["sales_trend"];
    top_products: BusinessDashboardResponse["data"]["top_products"];
  };
  meta: { generated_at: string; cache_ttl_seconds: number };
};

/** Aggregated GET /api/dashboard/alerts */
export type BusinessDashboardAlertsResponse = {
  data: {
    alerts: BusinessDashboardResponse["data"]["alerts"];
  };
  meta: { generated_at: string; cache_ttl_seconds: number };
};

/** Aggregated GET /api/dashboard/admin */
export type AdminDashboardResponse = {
  data: {
    platform_overview: {
      total_businesses: number;
      verified_businesses: number;
      pending_businesses: number;
      total_gmv: string;
      total_orders: number;
    };
    business_performance: Array<{
      business_id: string;
      business_name: string;
      total_revenue: string;
      total_orders: number;
      last_activity: string | null;
    }>;
    recent_activity: Array<{
      type: string;
      reference: string;
      label: string;
      amount: string | null;
      created_at: string | null;
      customer?: string | null;
    }>;
    insights: unknown[] | null;
  };
  meta: { generated_at: string; cache_ttl_seconds: number; from_date?: string; to_date?: string };
};

export type AuditAction =
  | "business_verified"
  | "business_rejected"
  | "impersonation_started"
  | "impersonation_ended";

export type AuditEventRow = {
  id: string;
  actorUserId: string;
  actorEmail: string;
  actorName: string;
  action: AuditAction;
  businessId?: string;
  businessName?: string;
  targetUserId?: string;
  timestamp: string;
};

export type AccountStatus = "active" | "suspended";

type DbUser = {
  id: string;
  name: string;
  email: string;
  password: string;
  role: Role;
  businessId: string | null;
  lastLoginAt?: string;
  accountStatus: AccountStatus;
};

type DbBusiness = {
  id: string;
  name: string;
  status: BusinessStatus;
  ownerUserId: string;
};

type DbSession = {
  token: string;
  userId: string;
  createdAt: number;
  impersonatorUserId?: string | null;
};

type MockDb = {
  users: DbUser[];
  businesses: DbBusiness[];
  sessions: DbSession[];
  auditEvents: AuditEventRow[];
  meta: { seededAt: number; version: 1 | 2 };
};

const DB_STORAGE_KEY = "upLevoPos_mock_db_v1";

const DEFAULT_SUPER_ADMIN = {
  email: "admin@uplevo.test",
  password: "Admin123!",
  name: "Super Admin",
} as const;

const DEFAULT_BUSINESS_OWNER = {
  email: "owner@uplevo.test",
  password: "Owner123!",
  name: "Business Owner",
  businessName: "UpLevo Demo Store",
  businessStatus: "verified" as const,
} as const;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function randomId(prefix: string) {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function migrateDb(parsed: MockDb): MockDb {
  let changed = false;
  if (!Array.isArray(parsed.auditEvents)) {
    parsed.auditEvents = [];
    changed = true;
  }
  if (!parsed.meta) {
    parsed.meta = { seededAt: Date.now(), version: 2 };
    changed = true;
  } else if (parsed.meta.version === 1) {
    parsed.meta = { ...parsed.meta, version: 2 };
    changed = true;
  }
  for (const u of parsed.users) {
    if (!u.accountStatus) {
      u.accountStatus = "active";
      changed = true;
    }
  }
  for (const s of parsed.sessions) {
    if (s.impersonatorUserId === undefined) {
      s.impersonatorUserId = null;
      changed = true;
    }
  }
  if (changed) writeDb(parsed);
  return parsed;
}

function readDb(): MockDb {
  try {
    const raw = localStorage.getItem(DB_STORAGE_KEY);
    if (!raw) return seedDb();
    const parsed = JSON.parse(raw) as MockDb;
    if (!parsed?.users || !parsed?.businesses || !parsed?.sessions) return seedDb();

    let changed = false;

    const superAdmin = parsed.users.find((u) => u.email.toLowerCase() === DEFAULT_SUPER_ADMIN.email);
    if (!superAdmin) {
      parsed.users.push({
        id: randomId("user"),
        name: DEFAULT_SUPER_ADMIN.name,
        email: DEFAULT_SUPER_ADMIN.email,
        password: DEFAULT_SUPER_ADMIN.password,
        role: "super_admin",
        businessId: null,
        accountStatus: "active",
      });
      changed = true;
    }

    const owner = parsed.users.find((u) => u.email.toLowerCase() === DEFAULT_BUSINESS_OWNER.email);
    if (!owner) {
      const ownerUserId = randomId("user");
      const ownerBusinessId = randomId("biz");
      parsed.users.push({
        id: ownerUserId,
        name: DEFAULT_BUSINESS_OWNER.name,
        email: DEFAULT_BUSINESS_OWNER.email,
        password: DEFAULT_BUSINESS_OWNER.password,
        role: "business_owner",
        businessId: ownerBusinessId,
        accountStatus: "active",
      });
      parsed.businesses.push({
        id: ownerBusinessId,
        name: DEFAULT_BUSINESS_OWNER.businessName,
        status: DEFAULT_BUSINESS_OWNER.businessStatus,
        ownerUserId,
      });
      changed = true;
    }

    if (changed) {
      writeDb(parsed);
    }

    return migrateDb(parsed);
  } catch {
    return seedDb();
  }
}

function writeDb(db: MockDb) {
  localStorage.setItem(DB_STORAGE_KEY, JSON.stringify(db));
}

function seedDb(): MockDb {
  const superAdminUserId = randomId("user");
  const ownerUserId = randomId("user");
  const ownerBusinessId = randomId("biz");
  const db: MockDb = {
    users: [
      {
        id: superAdminUserId,
        name: DEFAULT_SUPER_ADMIN.name,
        email: DEFAULT_SUPER_ADMIN.email,
        password: DEFAULT_SUPER_ADMIN.password,
        role: "super_admin",
        businessId: null,
        accountStatus: "active",
      },
      {
        id: ownerUserId,
        name: DEFAULT_BUSINESS_OWNER.name,
        email: DEFAULT_BUSINESS_OWNER.email,
        password: DEFAULT_BUSINESS_OWNER.password,
        role: "business_owner",
        businessId: ownerBusinessId,
        accountStatus: "active",
      },
    ],
    businesses: [
      {
        id: ownerBusinessId,
        name: DEFAULT_BUSINESS_OWNER.businessName,
        status: DEFAULT_BUSINESS_OWNER.businessStatus,
        ownerUserId,
      },
    ],
    sessions: [],
    auditEvents: [],
    meta: { seededAt: Date.now(), version: 2 },
  };
  try {
    writeDb(db);
  } catch {
    // ignore
  }
  return db;
}

function toAuthUser(u: DbUser): AuthUser {
  return { id: u.id, name: u.name, email: u.email, role: u.role };
}

function toAuthBusiness(b: DbBusiness): AuthBusiness {
  return { id: b.id, name: b.name, status: b.status };
}

function findSession(db: MockDb, token: string) {
  return db.sessions.find((s) => s.token === token) ?? null;
}

function requireSession(db: MockDb, token: string) {
  const sess = findSession(db, token);
  if (!sess) throw new ApiError({ code: "UNAUTHENTICATED", message: "Invalid session" });
  const user = db.users.find((u) => u.id === sess.userId) ?? null;
  if (!user) throw new ApiError({ code: "UNAUTHENTICATED", message: "User missing" });
  return { session: sess, user };
}

function assertNotImpersonationWrite(db: MockDb, token: string) {
  const sess = findSession(db, token);
  if (sess?.impersonatorUserId) {
    throw new ApiError({ code: "READ_ONLY_IMPERSONATION", message: "Read-only while viewing as business" });
  }
}

function createSession(db: MockDb, userId: string, impersonatorUserId: string | null = null) {
  const token = randomId("tok");
  const session: DbSession = { token, userId, createdAt: Date.now(), impersonatorUserId };
  db.sessions.push(session);
  return session;
}

function appendAudit(
  db: MockDb,
  actor: DbUser,
  action: AuditAction,
  opts: { businessId?: string; businessName?: string; targetUserId?: string },
) {
  const row: AuditEventRow = {
    id: randomId("aud"),
    actorUserId: actor.id,
    actorEmail: actor.email,
    actorName: actor.name,
    action,
    businessId: opts.businessId,
    businessName: opts.businessName,
    targetUserId: opts.targetUserId,
    timestamp: new Date().toISOString(),
  };
  db.auditEvents.unshift(row);
}

function authResultOrBusinessNotVerified(user: DbUser, session: DbSession, business: DbBusiness | null) {
  const result = { token: session.token, user: toAuthUser(user), business: business ? toAuthBusiness(business) : null };
  if (user.role === "business_owner" && business && business.status !== "verified") {
    throw new ApiError({
      code: "BUSINESS_NOT_VERIFIED",
      message: "Business is not verified",
      data: result,
    });
  }
  return result;
}

export type MeResponse = {
  token: string;
  user: AuthUser;
  business: AuthBusiness | null;
  lastLoginAt: string | null;
  sessionStartedAt: number;
  impersonation: {
    actorUserId: string;
    actorEmail: string;
    actorName: string;
    targetUserId: string;
    targetBusinessId: string | null;
    startedAt: number;
  } | null;
};

function buildMeResponse(db: MockDb, _token: string, session: DbSession, user: DbUser, business: DbBusiness | null): MeResponse {
  const base = {
    token: session.token,
    user: toAuthUser(user),
    business: business ? toAuthBusiness(business) : null,
  };
  const actor =
    session.impersonatorUserId ? db.users.find((u) => u.id === session.impersonatorUserId) ?? null : null;
  return {
    ...base,
    lastLoginAt: user.lastLoginAt ?? null,
    sessionStartedAt: session.createdAt,
    impersonation:
      actor && session.impersonatorUserId
        ? {
            actorUserId: actor.id,
            actorEmail: actor.email,
            actorName: actor.name,
            targetUserId: user.id,
            targetBusinessId: user.businessId,
            startedAt: session.createdAt,
          }
        : null,
  };
}

export const api = {
  auth: {
    async register(payload: RegisterPayload) {
      const res = await requestJson<{ token: string; user: AuthUser; business: AuthBusiness; status?: string }>(
        "/api/auth/register",
        {
          method: "POST",
          body: {
            name: payload.name,
            email: payload.email,
            password: payload.password,
            businessName: payload.businessName,
          },
        },
      );

      return { token: res.token, user: res.user, business: res.business };
    },

    async login({ email, password }: { email: string; password: string }) {
      const res = await requestJson<{ token: string; user: AuthUser; business: AuthBusiness | null; roles: Role[] }>(
        "/api/auth/login",
        {
          method: "POST",
          body: { email, password },
        },
      );

      return { token: res.token, user: res.user, business: res.business };
    },

    async me({ token }: { token: string }): Promise<MeResponse> {
      const me = await requestJson<{ user: AuthUser; business: AuthBusiness | null; roles: Role[] }>(
        "/api/auth/me",
        { method: "GET", token },
      );

      // The backend `me` endpoint doesn't return token/impersonation/session fields,
      // but the UI currently expects the legacy `MeResponse` shape.
      return {
        token,
        user: me.user,
        business: me.business,
        lastLoginAt: null,
        sessionStartedAt: 0,
        impersonation: null,
      };
    },

    async logout({ token }: { token: string }): Promise<void> {
      await requestJson<{ message?: string }>(
        "/api/auth/logout",
        { method: "POST", token },
      );
    },
  },

  admin: {
    async getBusinesses({
      token,
      status,
      q,
    }: {
      token: string;
      status?: BusinessStatus;
      q?: string;
    }) {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q?.trim()) params.set("q", q.trim());

      const qs = params.toString();
      const path = `/api/admin/businesses${qs ? `?${qs}` : ""}`;

      return requestJson<
        Array<{
          id: string;
          name: string;
          status: BusinessStatus;
          ownerUserId: string;
          ownerEmail: string | null;
          ownerName: string | null;
          rejectionReason?: string | null;
          verifiedAt?: string | null;
        }>
      >(path, { method: "GET", token });
    },

    async listBusinessProfiles({
      token,
      q,
      minCompletion,
      maxCompletion,
      page = 1,
      perPage = 100,
    }: {
      token: string;
      q?: string;
      minCompletion?: number;
      maxCompletion?: number;
      page?: number;
      perPage?: number;
    }) {
      const params = new URLSearchParams();
      if (q?.trim()) params.set("q", q.trim());
      if (typeof minCompletion === "number") params.set("min_completion", String(minCompletion));
      if (typeof maxCompletion === "number") params.set("max_completion", String(maxCompletion));
      params.set("page", String(page));
      params.set("per_page", String(perPage));

      const qs = params.toString();
      const path = `/api/admin/business-profiles${qs ? `?${qs}` : ""}`;

      return requestJson<{
        data: Array<{
          business_id: string;
          business_name: string;
          business_status: BusinessStatus;
          owner_email: string | null;
          owner_name: string | null;
          store_type: string | null;
          profile_data: Record<string, unknown> | null;
          completed_steps: string[] | null;
          completion_percentage: number;
          is_complete: boolean;
          last_updated_at?: string | null;
          updated_after_verification?: boolean;
        }>;
        meta: { current_page: number; last_page: number; per_page: number; total: number };
      }>(path, { method: "GET", token });
    },

    async getBusinessProfileDetail({ token, businessId }: { token: string; businessId: string }) {
      const path = `/api/admin/business-profiles/${encodeURIComponent(businessId)}`;
      return requestJson<{
        business_id: string;
        business_name: string;
        business_status: BusinessStatus;
        owner_email: string | null;
        owner_name: string | null;
        store_type: string | null;
        profile_data: Record<string, unknown>;
        completed_steps: string[];
        completion_percentage: number;
        is_complete: boolean;
        last_updated_at: string | null;
        updated_after_verification: boolean;
        last_updated_by_user_id: string | null;
        last_updated_by_email: string | null;
      }>(path, { method: "GET", token });
    },

    async listNotifications({
      token,
      page = 1,
      perPage = 20,
    }: {
      token: string;
      page?: number;
      perPage?: number;
    }) {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", String(perPage));
      const path = `/api/admin/notifications?${params.toString()}`;
      return requestJson<{
        data: Array<{
          id: string;
          type: string;
          reference_id: string;
          message: string;
          created_at: string | null;
        }>;
        meta: { current_page: number; last_page: number; per_page: number; total: number };
      }>(path, { method: "GET", token });
    },

    // Backward-compatible alias for existing pages.
    async listBusinesses({
      token,
      status,
      q,
    }: {
      token: string;
      status?: BusinessStatus;
      q?: string;
    }) {
      return this.getBusinesses({ token, status, q });
    },

    async listUsers({
      token,
      q,
      status,
      page = 1,
      pageSize = 20,
    }: {
      token: string;
      q?: string;
      status?: AccountStatus;
      page?: number;
      pageSize?: number;
    }) {
      await sleep(200);
      const db = readDb();
      const { user } = requireSession(db, token);
      if (user.role !== "super_admin") throw new ApiError({ code: "FORBIDDEN", message: "Forbidden" });

      let list = db.users.filter((u) => u.role === "business_owner");
      if (status) {
        list = list.filter((u) => u.accountStatus === status);
      }
      if (q?.trim()) {
        const qq = q.trim().toLowerCase();
        list = list.filter((u) => {
          const biz = u.businessId ? db.businesses.find((b) => b.id === u.businessId) : null;
          return (
            u.name.toLowerCase().includes(qq) ||
            u.email.toLowerCase().includes(qq) ||
            (biz?.name.toLowerCase().includes(qq) ?? false)
          );
        });
      }
      const total = list.length;
      const start = (page - 1) * pageSize;
      const items = list.slice(start, start + pageSize).map((u) => {
        const biz = u.businessId ? db.businesses.find((b) => b.id === u.businessId) ?? null : null;
        return {
          id: u.id,
          name: u.name,
          email: u.email,
          accountStatus: u.accountStatus,
          businessId: u.businessId,
          businessName: biz?.name ?? null,
          businessStatus: biz?.status ?? null,
        };
      });
      return { items, total, page, pageSize };
    },

    async listAuditEvents({ token, limit = 50 }: { token: string; limit?: number }) {
      await sleep(150);
      const db = readDb();
      const { user } = requireSession(db, token);
      if (user.role !== "super_admin") throw new ApiError({ code: "FORBIDDEN", message: "Forbidden" });
      return db.auditEvents.slice(0, limit);
    },

    async verifyBusiness({
      token,
      id,
    }: {
      token: string;
      id: string;
    }): Promise<{ id: string; status: BusinessStatus }> {
      const path = `/api/admin/businesses/${encodeURIComponent(id)}/verify`;
      const res = await requestJson<{ id: string; status: BusinessStatus }>(path, { method: "POST", token });
      return res;
    },

    async rejectBusiness({
      token,
      id,
      reason,
    }: {
      token: string;
      id: string;
      reason: string;
    }): Promise<{ id: string; status: BusinessStatus }> {
      const path = `/api/admin/businesses/${encodeURIComponent(id)}/reject`;
      const res = await requestJson<{ id: string; status: BusinessStatus }>(path, {
        method: "POST",
        token,
        body: { rejection_reason: reason },
      });
      return res;
    },

    async startImpersonation({ token, targetUserId }: { token: string; targetUserId: string }) {
      await sleep(200);
      const db = readDb();
      const { user: actor, session: actorSession } = requireSession(db, token);
      if (actor.role !== "super_admin") throw new ApiError({ code: "FORBIDDEN", message: "Forbidden" });
      if (findSession(db, token)?.impersonatorUserId) {
        throw new ApiError({ code: "FORBIDDEN", message: "Already impersonating" });
      }

      const target = db.users.find((u) => u.id === targetUserId) ?? null;
      if (!target || target.role !== "business_owner") {
        throw new ApiError({ code: "NOT_FOUND", message: "Business owner not found" });
      }
      const biz = target.businessId ? db.businesses.find((b) => b.id === target.businessId) ?? null : null;
      if (!biz || biz.status !== "verified") {
        throw new ApiError({ code: "FORBIDDEN", message: "Business must be verified to view as" });
      }

      appendAudit(db, actor, "impersonation_started", {
        businessId: biz.id,
        businessName: biz.name,
        targetUserId: target.id,
      });

      db.sessions = db.sessions.filter((s) => s.token !== token);
      const newSess = createSession(db, target.id, actor.id);
      writeDb(db);

      return buildMeResponse(db, newSess.token, newSess, target, biz);
    },

    async endImpersonation({ token }: { token: string }) {
      await sleep(200);
      const db = readDb();
      const { session: sess, user } = requireSession(db, token);
      if (!sess.impersonatorUserId) {
        throw new ApiError({ code: "FORBIDDEN", message: "Not impersonating" });
      }
      const actor = db.users.find((u) => u.id === sess.impersonatorUserId) ?? null;
      if (!actor) throw new ApiError({ code: "UNAUTHENTICATED", message: "Actor missing" });

      appendAudit(db, actor, "impersonation_ended", {
        targetUserId: user.id,
        businessId: user.businessId ?? undefined,
        businessName: user.businessId ? db.businesses.find((b) => b.id === user.businessId)?.name : undefined,
      });

      db.sessions = db.sessions.filter((s) => s.token !== token);
      const newSess = createSession(db, actor.id, null);
      writeDb(db);

      const actorBusiness = null;
      return buildMeResponse(db, newSess.token, newSess, actor, actorBusiness);
    },
  },

  business: {
    async getMyBusiness({ token }: { token: string }): Promise<{
      business: { id: string; name: string; status: BusinessStatus; rejectionReason?: string | null };
      status: BusinessStatus;
    }> {
      const res = await requestJson<{ business: any; status: BusinessStatus }>("/api/business/me", { method: "GET", token });
      return {
        business: {
          id: res.business.id,
          name: res.business.name,
          status: res.business.status,
          rejectionReason: res.business.rejectionReason ?? null,
        },
        status: res.status,
      };
    },
  },

  businessProfile: {
    async get({ token }: { token: string }) {
      return requestJson<{
        store_type: string | null;
        profile_data: Record<string, Record<string, unknown>>;
        completed_steps: string[];
        completion_percentage: number;
        is_complete: boolean;
        next_step: string | null;
        updated_after_verification: boolean;
        last_updated_at: string | null;
      }>("/api/business/profile", { method: "GET", token });
    },

    async getProgress({ token }: { token: string }) {
      return requestJson<{
        completion_percentage: number;
        is_complete: boolean;
        next_step: string | null;
      }>("/api/business-profile/progress", { method: "GET", token });
    },

    async saveStep({
      token,
      step,
      body,
    }: {
      token: string;
      step: string;
      body: Record<string, unknown>;
    }) {
      return requestJson<{
        store_type: string | null;
        profile_data: Record<string, Record<string, unknown>>;
        completed_steps: string[];
        completion_percentage: number;
        is_complete: boolean;
        next_step: string | null;
        updated_after_verification: boolean;
        last_updated_at: string | null;
      }>(`/api/business-profile/steps/${encodeURIComponent(step)}`, {
        method: "POST",
        token,
        body,
      });
    },

    async update({
      token,
      profileData,
      storeType,
    }: {
      token: string;
      profileData: Record<string, Record<string, unknown>>;
      storeType?: string | null;
    }) {
      const body: Record<string, unknown> = { profile_data: profileData };
      if (storeType !== undefined && storeType !== null && storeType !== "") {
        body.store_type = storeType;
      }
      return requestJson<{
        store_type: string | null;
        profile_data: Record<string, Record<string, unknown>>;
        completed_steps: string[];
        completion_percentage: number;
        is_complete: boolean;
        next_step: string | null;
        updated_after_verification: boolean;
        last_updated_at: string | null;
      }>("/api/business/profile", { method: "PUT", token, body });
    },
  },

  dealers: {
    async list({ token }: { token: string }) {
      return requestJson<{ data: Array<{
        id: string;
        business_id: string;
        name: string;
        contact_person: string | null;
        phone: string | null;
        email: string | null;
        gst_number: string | null;
        address: string | null;
        lat?: string | null;
        lng?: string | null;
        place_id?: string | null;
        status: string | null;
        is_default?: boolean;
        is_system_generated?: boolean;
      }> }>("/api/dealers", { method: "GET", token });
    },

    async recommendations({
      token,
      payload,
    }: {
      token: string;
      payload: {
        items: Array<{ product_id: number | string; quantity?: number | string | null }>;
        lat?: number | string | null;
        lng?: number | string | null;
      };
    }) {
      return requestJson<{ data: Array<{
        dealer_id: number;
        name: string;
        distance_km: number | null;
        score: number;
        insights: string[];
      }> }>("/api/dealers/recommendations", { method: "POST", token, body: payload });
    },

    async create({
      token,
      payload,
    }: {
      token: string;
      payload: {
        name: string;
        status: string;
        contact_person?: string | null;
        phone?: string | null;
        email?: string | null;
        gst_number?: string | null;
        address?: string | null;
        lat?: number | string | null;
        lng?: number | string | null;
        place_id?: string | null;
      };
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        name: string;
        contact_person: string | null;
        phone: string | null;
        email: string | null;
        gst_number: string | null;
        address: string | null;
        lat?: string | null;
        lng?: string | null;
        place_id?: string | null;
        status: string | null;
        is_default?: boolean;
        is_system_generated?: boolean;
      }>("/api/dealers", { method: "POST", token, body: payload });
    },

    async update({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: Partial<{
        name: string | null;
        status: string | null;
        contact_person: string | null;
        phone: string | null;
        email: string | null;
        gst_number: string | null;
        address: string | null;
        lat: number | string | null;
        lng: number | string | null;
        place_id: string | null;
      }>;
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        name: string;
        contact_person: string | null;
        phone: string | null;
        email: string | null;
        gst_number: string | null;
        address: string | null;
        lat?: string | null;
        lng?: string | null;
        place_id?: string | null;
        status: string | null;
        is_default?: boolean;
        is_system_generated?: boolean;
      }>(`/api/dealers/${encodeURIComponent(String(id))}`, { method: "PUT", token, body: payload });
    },

    async remove({ token, id }: { token: string; id: string | number }) {
      await requestJson<unknown>(`/api/dealers/${encodeURIComponent(String(id))}`, { method: "DELETE", token });
    },
  },

  products: {
    async list({ token }: { token: string }) {
      return requestJson<{ data: Array<{
        id: string;
        business_id: string;
        name: string;
        sku: string | null;
        barcode?: string | null;
        barcode_source?: string | null;
        brand_id: string | null;
        status: string | null;
        category_ids: string[];
        is_gst_applicable?: boolean;
        gst_rate?: string | null;
        gst_type?: string | null;
        hsn_code?: string | null;
        available_qty_net?: string | null;
        pos_override_batch_id?: string | null;
        is_override?: boolean;
        pos_active_batch?: {
          id: string;
          received_at: string | null;
          quantity_remaining: string;
          selling_price: string;
          mrp: string;
          purchase_price: string;
        } | null;
        categories?: Array<{ id: string; name: string }> | null;
      }> }>("/api/products", { method: "GET", token });
    },

    async search({ token, search }: { token: string; search: string }) {
      const term = search.trim();
      const qs = new URLSearchParams();
      if (term) qs.set("search", term);
      return requestJson<{ data: Array<{ id: number; name: string; sku: string | null }> }>(
        `/api/products/search?${qs.toString()}`,
        { method: "GET", token },
      );
    },

    async getByBarcode({ token, barcode }: { token: string; barcode: string }) {
      const b = barcode.trim();
      const path = `/api/v1/products/by-barcode/${encodeURIComponent(b)}`;
      return requestJson<{ data: {
        id: string;
        business_id: string;
        name: string;
        sku: string | null;
        barcode?: string | null;
        brand_id: string | null;
        status: string | null;
        category_ids: string[];
        available_qty_net?: string | null;
        pos_override_batch_id?: string | null;
        is_override?: boolean;
        pos_active_batch?: any;
        categories?: Array<{ id: string; name: string }> | null;
      } }>(path, { method: "GET", token });
    },

    async resolveBarcode({ token, barcode }: { token: string; barcode: string }) {
      const b = barcode.trim();
      const path = `/api/v1/products/resolve-barcode/${encodeURIComponent(b)}`;
      return requestJson<
        | {
            source: "local";
            data: {
              id: string;
              business_id: string;
              name: string;
              sku: string | null;
              barcode?: string | null;
              barcode_source?: string | null;
              brand_id: string | null;
              status: string | null;
              category_ids: string[];
              available_qty_net?: string | null;
              pos_override_batch_id?: string | null;
              is_override?: boolean;
              pos_active_batch?: unknown;
              categories?: Array<{ id: string; name: string }> | null;
            };
          }
        | {
            source: "external";
            data: {
              name: string;
              brand: string | null;
              image: string | null;
              category: string | null;
              barcode: string;
            };
          }
      >(path, { method: "GET", token });
    },

    async generateBarcode({ token, id }: { token: string; id: string | number }) {
      const path = `/api/v1/products/${encodeURIComponent(String(id))}/generate-barcode`;
      return requestJson<{ barcode: string; already_exists: boolean }>(path, { method: "POST", token });
    },

    async printBarcodes({
      token,
      product_ids,
      copies,
    }: {
      token: string;
      product_ids: Array<number | string>;
      copies: number;
    }) {
      // NOTE: this endpoint returns PDF bytes, not JSON.
      const res = await fetch(`${API_BASE_URL}/api/v1/products/barcode/print`, {
        method: "POST",
        headers: {
          // Force JSON expectation so auth middleware returns 401/422 (not 302 redirects).
          // The server can still respond with application/pdf; fetch will accept it.
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          product_ids: product_ids.map((v) => Number(v)),
          copies,
        }),
      });

      if (!res.ok) {
        // Some Laravel stacks may still redirect on unauthenticated when request isn't recognized as JSON.
        // Convert that into a clean API error for the UI.
        if (res.status === 302) {
          throw new ApiError({
            code: "UNAUTHENTICATED",
            message: "Not authenticated (session redirect)",
            data: { raw: await res.text() },
          });
        }
        const raw = await res.text();
        let json: any = null;
        if (raw) {
          try {
            json = JSON.parse(raw);
          } catch {
            json = null;
          }
        }
        const code = json?.error ?? json?.code ?? `HTTP_${res.status}`;
        const message = json?.message ?? json?.error ?? res.statusText;
        throw new ApiError({
          code,
          message,
          data: { errors: json?.errors, raw: json },
        });
      }

      const blob = await res.blob();
      return blob;
    },

    async create({
      token,
      payload,
    }: {
      token: string;
      payload: {
        name: string;
        sku?: string | null;
        barcode?: string | null;
        barcode_source?: "local" | "external" | "manual" | "generated";
        brand_id?: number | string | null;
        status?: string | null;
        category_ids?: Array<number | string>;
        is_gst_applicable?: boolean;
        gst_rate?: number | null;
        gst_type?: "inclusive" | "exclusive" | null;
        hsn_code?: string | null;
      };
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        name: string;
        sku: string | null;
        barcode?: string | null;
        barcode_source?: string | null;
        brand_id: string | null;
        status: string | null;
        category_ids: string[];
      }>("/api/products", { method: "POST", token, body: payload });
    },

    async quickCreate({
      token,
      body,
    }: {
      token: string;
      body: {
        name: string;
        barcode: string;
        brand_name?: string | null;
        category_name?: string | null;
        purchase_price: number | string;
        selling_price: number | string;
        quantity: number | string;
        dealer_id?: number | string | null;
        barcode_source?: "local" | "external" | "manual" | "generated";
      };
    }) {
      return requestJson<{
        product: {
          id: string;
          business_id: string;
          name: string;
          slug: string;
          sku: string | null;
          barcode?: string | null;
          barcode_source?: string | null;
          brand_id: string | null;
          status: string | null;
          category_ids: string[];
          available_qty_net?: string | null;
          pos_override_batch_id?: string | null;
          is_override?: boolean;
          pos_active_batch?: unknown;
          categories?: Array<{ id: string; name: string }> | null;
        };
        stock_added: boolean;
        stock_available: boolean;
      }>("/api/products/quick-create", { method: "POST", token, body });
    },

    async update({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: Partial<{
        name: string | null;
        sku: string | null;
        brand_id: number | string | null;
        status: string | null;
        category_ids: Array<number | string>;
        is_gst_applicable?: boolean;
        gst_rate?: number | null;
        gst_type?: "inclusive" | "exclusive" | null;
        hsn_code?: string | null;
      }>;
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        name: string;
        sku: string | null;
        brand_id: string | null;
        status: string | null;
        category_ids: string[];
      }>(`/api/products/${encodeURIComponent(String(id))}`, { method: "PUT", token, body: payload });
    },

    async remove({ token, id }: { token: string; id: string | number }) {
      await requestJson<unknown>(`/api/products/${encodeURIComponent(String(id))}`, { method: "DELETE", token });
    },

    async listStockBatches({ token, productId }: { token: string; productId: string | number }) {
      const path = `/api/v1/products/${encodeURIComponent(String(productId))}/stock-batches`;
      return requestJson<{ data: Array<{
        id: string;
        received_at: string | null;
        quantity_remaining: string;
        selling_price: string;
        mrp: string;
        purchase_price: string;
      }> }>(path, { method: "GET", token });
    },

    async setPosBatch({
      token,
      productId,
      batchId,
    }: {
      token: string;
      productId: string | number;
      batchId: string | number;
    }) {
      const path = `/api/v1/products/${encodeURIComponent(String(productId))}/pos-batch`;
      return requestJson<{
        id: string;
        business_id: string;
        name: string;
        sku: string | null;
        brand_id: string | null;
        status: string | null;
        category_ids: string[];
        available_qty_net?: string | null;
        pos_override_batch_id?: string | null;
        is_override?: boolean;
        pos_active_batch?: any;
      }>(path, { method: "PUT", token, body: { batch_id: Number(batchId) } });
    },

    async clearPosBatch({ token, productId }: { token: string; productId: string | number }) {
      const path = `/api/v1/products/${encodeURIComponent(String(productId))}/pos-batch`;
      return requestJson<{
        id: string;
        business_id: string;
        name: string;
        sku: string | null;
        brand_id: string | null;
        status: string | null;
        category_ids: string[];
        available_qty_net?: string | null;
        pos_override_batch_id?: string | null;
        is_override?: boolean;
        pos_active_batch?: any;
      }>(path, { method: "DELETE", token });
    },
  },

  categories: {
    async list({ token }: { token: string }) {
      return requestJson<{ data: Array<{ id: string; parent_id: string | null; name: string; status: string | null; product_count?: number }> }>(
        "/api/categories",
        { method: "GET", token },
      );
    },
    async create({
      token,
      payload,
    }: {
      token: string;
      payload: { name: string; parent_id: string | null };
    }) {
      return requestJson<{ id: string; parent_id: string | null; name: string; status: string | null }>(
        "/api/categories",
        { method: "POST", token, body: payload },
      );
    },
    async update({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: Partial<{ name: string | null; parent_id: string | null }>;
    }) {
      return requestJson<{ id: string; parent_id: string | null; name: string; status: string | null }>(
        `/api/categories/${encodeURIComponent(String(id))}`,
        { method: "PUT", token, body: payload },
      );
    },
    async remove({ token, id }: { token: string; id: string | number }) {
      await requestJson<unknown>(`/api/categories/${encodeURIComponent(String(id))}`, {
        method: "DELETE",
        token,
      });
    },
    async bulkAssignParent({ token, ids, parent_id }: { token: string; ids: Array<string | number>; parent_id: string | number | null }) {
      return requestJson<{ ok: true }>("/api/categories/bulk/assign-parent", {
        method: "POST",
        token,
        body: { ids: ids.map((x) => Number(x)), parent_id: parent_id === null ? null : Number(parent_id) },
      });
    },
    async bulkDelete({ token, ids }: { token: string; ids: Array<string | number> }) {
      return requestJson<{ ok: true }>("/api/categories/bulk/delete", {
        method: "POST",
        token,
        body: { ids: ids.map((x) => Number(x)) },
      });
    },
  },

  brands: {
    async list({ token }: { token: string }) {
      return requestJson<{ data: Array<{ id: string; name: string; status: string | null; product_count?: number }> }>("/api/brands", {
        method: "GET",
        token,
      });
    },
    async create({ token, payload }: { token: string; payload: { name: string } }) {
      return requestJson<{ id: string; name: string; status: string | null }>("/api/brands", {
        method: "POST",
        token,
        body: payload,
      });
    },
    async update({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: Partial<{ name: string | null }>;
    }) {
      return requestJson<{ id: string; name: string; status: string | null }>(
        `/api/brands/${encodeURIComponent(String(id))}`,
        { method: "PUT", token, body: payload },
      );
    },
    async remove({ token, id }: { token: string; id: string | number }) {
      await requestJson<unknown>(`/api/brands/${encodeURIComponent(String(id))}`, { method: "DELETE", token });
    },
    async bulkDelete({ token, ids }: { token: string; ids: Array<string | number> }) {
      return requestJson<{ ok: true }>(`/api/brands/bulk/delete`, { method: "POST", token, body: { ids: ids.map((x) => Number(x)) } });
    },
  },

  inventory: {
    async list({ token }: { token: string }) {
      return requestJson<{ data: Array<{
        id: string;
        business_id: string;
        dealer_id: string;
        reference_no: string;
        total_amount: string;
        batch_ids: string[];
        created_at: string | null;
      }> }>("/api/inventory", { method: "GET", token });
    },

    async create({
      token,
      payload,
    }: {
      token: string;
      payload: {
        dealer_id: number | string;
        reference_no: string;
        total_amount: number | string;
        items: Array<{
          product_id: number | string;
          quantity: number | string;
          purchase_price: number | string;
          selling_price: number | string;
          mrp: number | string;
        }>;
      };
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer_id: string;
        reference_no: string;
        total_amount: string;
        items: Array<{
          product_id: string;
          quantity: string;
          purchase_price: string;
          selling_price: string;
          mrp: string;
        }>;
        created_at: string | null;
      }>("/api/inventory", { method: "POST", token, body: payload });
    },

    async bulkValidate({
      token,
      file,
    }: {
      token: string;
      file: File;
    }): Promise<{
      valid_rows: Array<{
        row_number: number;
        product_id: string | null;
        is_new_product: boolean;
        sku: string;
        product_name: string;
        quantity: string;
        purchase_price: string;
        selling_price: string;
        mrp: string;
        batch_no: string | null;
        expiry_date: string | null;
        brand: string;
        categories: string;
      }>;
      invalid_rows: Array<{
        row_number: number;
        errors: string[];
      }>;
    }> {
      const formData = new FormData();
      formData.append("csv", file);

      return requestFormData<{
        valid_rows: Array<{
          row_number: number;
          product_id: string | null;
          is_new_product: boolean;
          sku: string;
          product_name: string;
          quantity: string;
          purchase_price: string;
          selling_price: string;
          mrp: string;
          batch_no: string | null;
          expiry_date: string | null;
          brand: string;
          categories: string;
        }>;
        invalid_rows: Array<{
          row_number: number;
          errors: string[];
        }>;
      }>("/api/inventory/bulk/validate", { token, formData });
    },

    async bulkCommit({
      token,
      payload,
    }: {
      token: string;
      payload: {
        dealer_id: number | string;
        rows: Array<{
          row_number: number;
          is_new_product: boolean;
          product_id: number | string | null;
          sku: string;
          product_name: string;
          brand: string;
          categories: string;
          quantity: number | string;
          purchase_price: number | string;
          selling_price: number | string;
          mrp: number | string;
          batch_no?: string | null;
          expiry_date: string | null;
        }>;
      };
    }): Promise<{
      success_count: number;
      failed_count: number;
      errors: Array<{
        row_number: number;
        reason: string;
      }>;
    }> {
      return requestJson<{
        success_count: number;
        failed_count: number;
        errors: Array<{
          row_number: number;
          reason: string;
        }>;
      }>("/api/inventory/bulk/commit", { method: "POST", token, body: payload });
    },

    async show({ token, id }: { token: string; id: string | number }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer_id: string;
        reference_no: string;
        total_amount: string;
        items: Array<{
          product_id: string;
          quantity: string;
          purchase_price: string;
          selling_price: string;
          mrp: string;
        }>;
        created_at: string | null;
      }>(`/api/inventory/${encodeURIComponent(String(id))}`, { method: "GET", token });
    },

    async invoiceUpload({
      token,
      file,
    }: {
      token: string;
      file: File;
    }): Promise<{
      upload_id: string;
      status: string;
    }> {
      const formData = new FormData();
      formData.append("file", file);
      return requestFormData<{ upload_id: string; status: string }>(`/api/inventory/invoice/upload`, { token, formData });
    },

    async invoicePreview({
      token,
      id,
    }: {
      token: string;
      id: string | number;
    }): Promise<{
      upload_id: string;
      status: string;
      data: {
        dealer: unknown;
        items: unknown[];
        confidence: unknown;
        suggestions: unknown;
        totals?: { subtotal?: number; tax?: number; grand_total?: number };
        gst?: {
          regime?: string;
          column_map?: string;
          invoice_summary?: Record<string, unknown>;
        };
      };
    }> {
      return requestJson<{
        upload_id: string;
        status: string;
        data: {
          dealer: unknown;
          items: unknown[];
          confidence: unknown;
          suggestions: unknown;
          totals?: { subtotal?: number; tax?: number; grand_total?: number };
          gst?: {
            regime?: string;
            column_map?: string;
            invoice_summary?: Record<string, unknown>;
          };
        };
      }>(`/api/inventory/invoice/${encodeURIComponent(String(id))}/preview`, { method: "GET", token });
    },

    async invoiceCommit({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: {
        reference_no: string;
        total_amount: number | string;
        gst_regime?: "igst" | "cgst_sgst" | "unknown";
        gst_meta?: {
          column_map?: string;
          invoice_summary?: {
            hsn_sac?: string;
            taxable_value?: number | string;
            central_tax_rate_pct?: number | string;
            central_tax_amount?: number | string;
            state_tax_rate_pct?: number | string;
            state_tax_amount?: number | string;
            integrated_tax_rate_pct?: number | string;
            integrated_tax_amount?: number | string;
            total_tax_amount?: number | string;
            total_quantity_display?: string;
            amount_chargeable_in_words?: string;
            tax_amount_in_words?: string;
          };
        } | null;
        dealer: {
          dealer_id?: number | string | null;
          name?: string | null;
          status?: string | null;
          contact_person?: string | null;
          phone?: string | null;
          email?: string | null;
          gst_number?: string | null;
          address?: string | null;
        };
        items: Array<{
          product_id?: number | string | null;
          name?: string | null;
          raw_name?: string | null;
          is_user_corrected?: boolean;
          qty: number | string;
          purchase_price: number | string;
          selling_price: number | string;
          mrp: number | string;
          batch_no?: string | null;
          expiry_date?: string | null;
          cgst_rate_pct?: number | string | null;
          cgst_amount?: number | string | null;
          sgst_rate_pct?: number | string | null;
          sgst_amount?: number | string | null;
          igst_rate_pct?: number | string | null;
          igst_amount?: number | string | null;
        }>;
      };
    }): Promise<{
      upload_id: string;
      status: string;
      inventory_entry_id: string | null;
    }> {
      return requestJson<{ upload_id: string; status: string; inventory_entry_id: string | null }>(
        `/api/inventory/invoice/${encodeURIComponent(String(id))}/commit`,
        { method: "POST", token, body: payload },
      );
    },
  },

  purchaseOrders: {
    async createPO({
      token,
      payload,
    }: {
      token: string;
      payload: {
        business_id?: number | string | null;
        dealer_id: number | string;
        expected_delivery_date?: string | null;
        notes?: string | null;
        items: Array<{
          product_id: number | string;
          ordered_qty: number | string;
          purchase_price: number | string;
        }>;
      };
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer: { id: string; name: string };
        status: string;
        expected_delivery_date: string | null;
        notes: string | null;
        items: Array<{
          product_id: string;
          product_name: string;
          ordered_qty: string;
          received_qty: string;
          purchase_price: string;
        }>;
        created_at: string | null;
      }>("/api/purchase-orders", { method: "POST", token, body: payload });
    },

    async listPO({
      token,
      status,
      dealerId,
    }: {
      token: string;
      status?: string;
      dealerId?: string | number;
    }) {
      const params = new URLSearchParams();
      if (status?.trim()) params.set("status", status.trim());
      if (dealerId !== undefined && dealerId !== null && String(dealerId) !== "") {
        params.set("dealer_id", String(dealerId));
      }
      const qs = params.toString();
      const path = `/api/purchase-orders${qs ? `?${qs}` : ""}`;
      return requestJson<{
        data: Array<{
          id: string;
          dealer: { id: string; name: string };
          status: string;
          ordered_qty_total: string;
          received_qty_total: string;
          expected_delivery_date: string | null;
          created_at: string | null;
        }>;
      }>(path, { method: "GET", token });
    },

    async getPO({ token, id }: { token: string; id: string | number }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer: { id: string; name: string };
        status: string;
        expected_delivery_date: string | null;
        notes: string | null;
        items: Array<{
          product_id: string;
          product_name: string;
          ordered_qty: string;
          received_qty: string;
          purchase_price: string;
        }>;
        created_at: string | null;
      }>(`/api/purchase-orders/${encodeURIComponent(String(id))}`, { method: "GET", token });
    },

    async updatePO({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: {
        dealer_id: number | string;
        expected_delivery_date?: string | null;
        notes?: string | null;
        items: Array<{
          product_id: number | string;
          ordered_qty: number | string;
          purchase_price: number | string;
        }>;
      };
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer: { id: string; name: string };
        status: string;
        expected_delivery_date: string | null;
        notes: string | null;
        items: Array<{
          product_id: string;
          product_name: string;
          ordered_qty: string;
          received_qty: string;
          purchase_price: string;
        }>;
        created_at: string | null;
      }>(`/api/purchase-orders/${encodeURIComponent(String(id))}`, { method: "PUT", token, body: payload });
    },

    async updatePOStatus({
      token,
      id,
      status,
    }: {
      token: string;
      id: string | number;
      status: string;
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer: { id: string; name: string };
        status: string;
        expected_delivery_date: string | null;
        notes: string | null;
        items: Array<{
          product_id: string;
          product_name: string;
          ordered_qty: string;
          received_qty: string;
          purchase_price: string;
        }>;
        created_at: string | null;
      }>(`/api/purchase-orders/${encodeURIComponent(String(id))}/status`, { method: "POST", token, body: { status } });
    },

    async receivePOItems({
      token,
      id,
      items,
    }: {
      token: string;
      id: string | number;
      items: Array<{
        product_id: number | string;
        received_qty: number | string;
        purchase_price?: number | string | null;
        selling_price: number | string;
        mrp: number | string;
      }>;
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        dealer: { id: string; name: string };
        status: string;
        expected_delivery_date: string | null;
        notes: string | null;
        items: Array<{
          product_id: string;
          product_name: string;
          ordered_qty: string;
          received_qty: string;
          purchase_price: string;
        }>;
        created_at: string | null;
      }>(`/api/purchase-orders/${encodeURIComponent(String(id))}/receive`, { method: "POST", token, body: { items } });
    },
  },

  posPricing: {
    async getProductPricing({ token, productId }: { token: string; productId: string | number }) {
      const path = `/api/v1/pos/products/${encodeURIComponent(String(productId))}/pricing`;
      return requestJson<{
        unit_sell_price: string | null;
        unit_mrp: string | null;
        available_qty_net: string;
        reserved_qty_total: string;
        your_reserved_qty: string;
        pricing_source: string;
        active_batch: null | {
          id: string;
          received_at: string | null;
          quantity_remaining: string;
        };
      }>(path, { method: "GET", token });
    },

    async getBatchPricing({ token, ids }: { token: string; ids: Array<string | number> }) {
      const csv = ids.map((v) => encodeURIComponent(String(v))).join(",");
      const path = `/api/v1/pos/products/pricing?ids=${csv}`;
      return requestJson<{
        data: Record<
          string,
          {
            unit_sell_price: string | null;
            unit_mrp: string | null;
            available_qty_net: string;
            reserved_qty_total: string;
            your_reserved_qty: string;
            pricing_source: string;
            is_override: boolean;
            active_batch: null | {
              id: string;
              received_at: string | null;
              quantity_remaining: string;
            };
          }
        >;
      }>(path, { method: "GET", token });
    },
  },

  customers: {
    async lookup({ token, phone }: { token: string; phone: string }) {
      const qs = new URLSearchParams({ phone });
      return requestJson<{
        data: { customer: null | { id: string; name: string; phone: string } };
      }>(`/api/v1/customers/lookup?${qs.toString()}`, { method: "GET", token });
    },
  },

  sales: {
    async create({
      token,
      payload,
    }: {
      token: string;
      payload: {
        items: Array<{ product_id: string | number; quantity: number }>;
        customer_name: string;
        customer_phone: string;
        payment_method: "cash" | "razorpay" | "gpay" | "upi";
        promo_code?: string | null;
      };
    }) {
      return requestJson<{
        id: string;
        business_id: string;
        user_id: string | null;
        customer_id: string;
        customer_name: string;
        customer_phone: string;
        payment_method: string;
        subtotal_amount: string;
        tax_amount: string;
        total_amount: string;
        items: Array<{
          product_id: string;
          quantity: string;
          line_total: string;
          taxable_amount?: string | null;
          tax_amount?: string | null;
          cgst_amount?: string | null;
          sgst_amount?: string | null;
          gst_rate?: string | null;
          gst_override_type?: string | null;
          gst_override_value?: string | null;
          is_tax_compliant?: boolean | null;
          batches: Array<{
            stock_batch_id: string;
            quantity: string;
            unit_selling_price: string;
            unit_mrp: string;
            unit_purchase_price: string;
          }>;
        }>;
        created_at: string | null;
      }>("/api/v1/sales", { method: "POST", token, body: payload });
    },

    async list({
      token,
      query,
    }: {
      token: string;
      query?: Partial<{
        page: number;
        per_page: number;
        date_from: string;
        date_to: string;
        search: string;
        payment_method: string;
        status: string;
      }>;
    }) {
      const params = new URLSearchParams();
      if (query?.page !== undefined) params.set("page", String(query.page));
      if (query?.per_page !== undefined) params.set("per_page", String(query.per_page));
      if (query?.date_from) params.set("date_from", query.date_from);
      if (query?.date_to) params.set("date_to", query.date_to);
      if (query?.search?.trim()) params.set("search", query.search.trim());
      if (query?.payment_method) params.set("payment_method", query.payment_method);
      if (query?.status) params.set("status", query.status);

      const qs = params.toString();
      const path = `/api/v1/sales${qs ? `?${qs}` : ""}`;

      return requestJson<{
        data: Array<{
          id: string;
          invoice_number: string | null;
          customer_name: string | null;
          customer_phone: string | null;
          items_count: number;
          subtotal: string;
          tax_amount: string;
          total_amount: string;
          payment_method: string;
          status: string;
          created_at: string | null;
        }>;
        meta: { current_page: number; last_page: number; per_page: number; total: number };
      }>(path, { method: "GET", token });
    },

    async detail({ token, id }: { token: string; id: string | number }) {
      return requestJson<{
        id: string;
        invoice_number: string | null;
        status: string;
        created_at: string | null;
        business: { name?: string | null; gstin?: string | null; address?: string | null; phone?: string | null };
        customer: { id?: string | null; name?: string | null; phone?: string | null; gstin?: string | null };
        items: Array<{
          id: string;
          product: { id: string; name: string | null; sku: string | null };
          hsn: string | null;
          gst_rate: string | null;
          quantity: string;
          unit_price: string | null;
          discount_amount: string;
          taxable_amount: string;
          tax_amount: string;
          line_total: string;
          batches: Array<{
            stock_batch_id: string;
            batch_no: string | null;
            expiry_date: string | null;
            quantity: string;
            unit_selling_price: string;
            unit_mrp: string;
          }>;
        }>;
        gst_breakdown: Array<{
          rate: string;
          taxable: string;
          cgst: string;
          sgst: string;
          igst: string;
          total_tax: string;
        }>;
        totals: {
          subtotal: string;
          discount: string;
          tax: string;
          taxable_amount: string;
          grand_total: string;
        };
        payment: { method: string; meta: unknown };
      }>(`/api/v1/sales/${encodeURIComponent(String(id))}`, { method: "GET", token });
    },

    async downloadInvoice({ token, id }: { token: string; id: string | number }) {
      const res = await fetch(`${API_BASE_URL}/api/v1/sales/${encodeURIComponent(String(id))}/invoice`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!res.ok) {
        if (res.status === 302) {
          throw new ApiError({
            code: "UNAUTHENTICATED",
            message: "Not authenticated (session redirect)",
            data: { raw: await res.text() },
          });
        }
        const raw = await res.text();
        let json: any = null;
        if (raw) {
          try {
            json = JSON.parse(raw);
          } catch {
            json = null;
          }
        }
        const code = json?.error ?? json?.code ?? `HTTP_${res.status}`;
        const message = json?.message ?? json?.error ?? res.statusText;
        throw new ApiError({
          code,
          message,
          data: { errors: json?.errors, raw: json },
        });
      }

      return await res.blob();
    },

    async exportSalesReport({
      token,
      query,
    }: {
      token: string;
      query?: Partial<{
        date_from: string;
        date_to: string;
        search: string;
        payment_method: string;
        status: string;
      }>;
    }) {
      const params = new URLSearchParams();
      if (query?.date_from) params.set("date_from", query.date_from);
      if (query?.date_to) params.set("date_to", query.date_to);
      if (query?.search?.trim()) params.set("search", query.search.trim());
      if (query?.payment_method) params.set("payment_method", query.payment_method);
      if (query?.status) params.set("status", query.status);

      const qs = params.toString();
      const res = await fetch(`${API_BASE_URL}/api/v1/sales/export${qs ? `?${qs}` : ""}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!res.ok) {
        if (res.status === 302) {
          throw new ApiError({
            code: "UNAUTHENTICATED",
            message: "Not authenticated (session redirect)",
            data: { raw: await res.text() },
          });
        }
        const raw = await res.text();
        let json: any = null;
        if (raw) {
          try {
            json = JSON.parse(raw);
          } catch {
            json = null;
          }
        }
        const code = json?.error ?? json?.code ?? `HTTP_${res.status}`;
        const message = json?.message ?? json?.error ?? res.statusText;
        throw new ApiError({
          code,
          message,
          data: { errors: json?.errors, raw: json },
        });
      }

      const filename = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "sales_report.csv";
      const blob = await res.blob();
      return { filename, blob };
    },
  },

  posCart: {
    async start({ token }: { token: string }) {
      return requestJson<{
        cart: {
          id: string;
          status: string;
          items: Array<{
            product_id: number;
            quantity: string;
            stock_batch_id?: number | null;
            gst_override_type?: string | null;
            gst_override_value?: number | string | null;
            gst_override_reason?: string | null;
          }>;
          created_at: string | null;
          updated_at: string | null;
        };
      }>("/api/v1/pos/cart/start", { method: "POST", token });
    },

    async current({ token }: { token: string }) {
      return requestJson<{ cart: null | {
        id: string;
        status: string;
        items: Array<{
          product_id: number;
          quantity: string;
          stock_batch_id?: number | null;
          gst_override_type?: string | null;
          gst_override_value?: number | string | null;
          gst_override_reason?: string | null;
        }>;
        created_at: string | null;
        updated_at: string | null;
      } }>("/api/v1/pos/cart/current", { method: "GET", token });
    },

    async updateCurrent({
      token,
      payload,
    }: {
      token: string;
      payload: {
        items: Array<{
          product_id: string | number;
          quantity: number;
          gst_override_type?: string | null;
          gst_override_value?: number | string | null;
          gst_override_reason?: string | null;
        }>;
      };
    }) {
      return requestJson<{
        cart: {
          id: string;
          status: string;
          items: Array<{
            product_id: number;
            quantity: string;
            stock_batch_id?: number | null;
            gst_override_type?: string | null;
            gst_override_value?: number | string | null;
            gst_override_reason?: string | null;
          }>;
          created_at: string | null;
          updated_at: string | null;
        };
      }>("/api/v1/pos/cart/current", { method: "PUT", token, body: payload });
    },

    async clear({ token }: { token: string }) {
      return requestJson<{ ok: boolean }>("/api/v1/pos/cart/clear", { method: "POST", token });
    },

    async quote({
      token,
      payload,
    }: {
      token: string;
      payload: {
        items: Array<{ product_id: string | number; quantity: number }>;
        promo_code?: string | null;
      };
    }) {
      return requestJson<{
        data: {
          applied_promotion: null | {
            id: string;
            name: string;
            code: string | null;
            type: string;
            priority: number;
          };
          totals: {
            subtotal: string;
            discount: string;
            taxable: string;
            gst: string;
            grand_total: string;
          };
          lines: Array<{
            product_id: string;
            quantity: string;
            base_line_total: string;
            discount_amount: string;
            discounted_line_total: string;
            gst: {
              taxable_amount: string;
              tax_amount: string;
              cgst_amount: string;
              sgst_amount: string;
              line_total: string;
              effective_gst_rate: string | null;
              is_tax_compliant: boolean;
            };
          }>;
        };
      }>("/api/v1/pos/cart/quote", { method: "POST", token, body: payload });
    },
  },

  promotions: {
    async list({ token }: { token: string }) {
      return requestJson<{ data: Array<{
        id: string;
        business_id: string;
        name: string;
        type: string;
        code: string | null;
        status: string;
        start_at: string | null;
        end_at: string | null;
        priority: number;
        rules: Array<{ id: string; rule_type: string; operator: string; value: unknown }>;
        benefits: Array<{ id: string; benefit_type: string; value: unknown }>;
        product_ids: string[];
        usage: null | { usage_limit: number | null; used_count: number };
      }> }>("/api/v1/promotions", { method: "GET", token });
    },

    async create({
      token,
      payload,
    }: {
      token: string;
      payload: {
        name: string;
        type: string;
        code?: string | null;
        status: string;
        start_at?: string | null;
        end_at?: string | null;
        priority?: number;
        rules?: Array<{ rule_type: string; operator: string; value: unknown }>;
        benefits?: Array<{ benefit_type: string; value: unknown }>;
        product_ids?: Array<string | number>;
        usage?: { usage_limit?: number | null; used_count?: number | null };
      };
    }) {
      return requestJson<{ data: any }>("/api/v1/promotions", { method: "POST", token, body: payload });
    },

    async update({
      token,
      id,
      payload,
    }: {
      token: string;
      id: string | number;
      payload: Partial<{
        name: string;
        type: string;
        code: string | null;
        status: string;
        start_at: string | null;
        end_at: string | null;
        priority: number;
        rules: Array<{ rule_type: string; operator: string; value: unknown }>;
        benefits: Array<{ benefit_type: string; value: unknown }>;
        product_ids: Array<string | number>;
        usage: { usage_limit?: number | null; used_count?: number | null };
      }>;
    }) {
      return requestJson<{ data: any }>(`/api/v1/promotions/${encodeURIComponent(String(id))}`, { method: "PUT", token, body: payload });
    },

    async toggle({
      token,
      id,
      status,
    }: {
      token: string;
      id: string | number;
      status: string;
    }) {
      return requestJson<{ data: any }>(`/api/v1/promotions/${encodeURIComponent(String(id))}/toggle`, { method: "POST", token, body: { status } });
    },

    stacking: {
      async get({ token }: { token: string }) {
        return requestJson<{
          data: {
            business_id: number;
            allow_combo_discount: boolean;
            allow_discount_coupon: boolean;
            allow_combo_coupon: boolean;
            allow_multiple_coupons: boolean;
            execution_order: string[];
            max_discount_percent: string | null;
            prevent_loss: boolean;
          };
        }>("/api/v1/promotions/stacking", { method: "GET", token });
      },

      async update({
        token,
        payload,
      }: {
        token: string;
        payload: Partial<{
          allow_combo_discount: boolean;
          allow_discount_coupon: boolean;
          allow_combo_coupon: boolean;
          allow_multiple_coupons: boolean;
          execution_order: string[];
          max_discount_percent: number | string | null;
          prevent_loss: boolean;
        }>;
      }) {
        return requestJson<{
          data: {
            business_id: number;
            allow_combo_discount: boolean;
            allow_discount_coupon: boolean;
            allow_combo_coupon: boolean;
            allow_multiple_coupons: boolean;
            execution_order: string[];
            max_discount_percent: string | null;
            prevent_loss: boolean;
          };
        }>("/api/v1/promotions/stacking", { method: "PUT", token, body: payload });
      },
    },
  },

  dashboard: {
    async getBusiness({ token }: { token: string }) {
      return requestJson<BusinessDashboardResponse>("/api/dashboard/business", { method: "GET", token });
    },

    async getSummary({ token }: { token: string }) {
      return requestJson<BusinessDashboardSummaryResponse>("/api/dashboard/summary", { method: "GET", token });
    },

    async getAnalytics({ token }: { token: string }) {
      return requestJson<BusinessDashboardAnalyticsResponse>("/api/dashboard/analytics", { method: "GET", token });
    },

    async getAlerts({ token }: { token: string }) {
      return requestJson<BusinessDashboardAlertsResponse>("/api/dashboard/alerts", { method: "GET", token });
    },

    async getAdmin({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null) {
        params.set("business_id", String(filters.business_id));
      }
      const qs = params.toString();
      const path = `/api/dashboard/admin${qs ? `?${qs}` : ""}`;
      return requestJson<AdminDashboardResponse>(path, { method: "GET", token });
    },
  },

  analytics: {
    async getSettings({ token }: { token: string }) {
      return requestJson<{
        data: { default_supplier_lead_time_days: number; safety_stock_days: number };
      }>("/api/analytics/settings", { method: "GET", token });
    },

    async updateSettings({
      token,
      body,
    }: {
      token: string;
      body: Partial<{ default_supplier_lead_time_days: number; safety_stock_days: number }>;
    }) {
      return requestJson<{
        data: { default_supplier_lead_time_days: number; safety_stock_days: number };
      }>("/api/analytics/settings", { method: "PATCH", token, body });
    },

    async getReorderSuggestions({
      token,
      query,
    }: {
      token: string;
      query?: Partial<{ window_days: number; lead_time_days: number; safety_stock_days: number }>;
    }) {
      const params = new URLSearchParams();
      if (query?.window_days !== undefined) params.set("window_days", String(query.window_days));
      if (query?.lead_time_days !== undefined) params.set("lead_time_days", String(query.lead_time_days));
      if (query?.safety_stock_days !== undefined) params.set("safety_stock_days", String(query.safety_stock_days));
      const qs = params.toString();
      return requestJson<{
        meta: {
          window_days: number;
          lead_time_days: number;
          safety_stock_days: number;
          observation_days: number;
          generated_at: string;
        };
        rows: Array<{
          product_id: string;
          product_name: string;
          on_hand: number;
          total_qty_sold_window?: number;
          avg_daily_sales: number | null;
          days_of_cover: number | null;
          severity: string;
          recommended_order_qty: number | null;
          order_by_date: string | null;
          revenue_window?: string;
        }>;
      }>(`/api/analytics/reorder-suggestions${qs ? `?${qs}` : ""}`, { method: "GET", token });
    },

    async getGrowthInsights({
      token,
      query,
    }: {
      token: string;
      query?: Partial<{ window_days: number }>;
    }) {
      const params = new URLSearchParams();
      if (query?.window_days !== undefined) params.set("window_days", String(query.window_days));
      const qs = params.toString();
      return requestJson<{
        category_momentum: Array<{
          category_id: string;
          category_name: string;
          revenue_current_window: number;
          revenue_previous_window: number;
          growth_percent: number;
        }>;
        basket_affinity: Array<{
          product_a_id: string;
          product_a_name: string;
          product_b_id: string;
          product_b_name: string;
          pair_count: number;
        }>;
        top_movers_low_cover: Array<{
          product_id: string;
          product_name: string;
          revenue_window: string;
          days_of_cover: number;
          severity: string;
        }>;
        meta: { window_days: number; generated_at: string };
      }>(`/api/analytics/growth-insights${qs ? `?${qs}` : ""}`, { method: "GET", token });
    },

    async getPeerRoadmap({ token }: { token: string }) {
      return requestJson<{
        data: { message: string; roadmap: string[] };
      }>("/api/analytics/peer-roadmap", { method: "GET", token });
    },

    async getAnalyticsSummary({
      token,
      query,
    }: {
      token: string;
      query?: Partial<{ window_days: number; lead_time_days: number; safety_stock_days: number }>;
    }) {
      const params = new URLSearchParams();
      if (query?.window_days !== undefined) params.set("window_days", String(query.window_days));
      if (query?.lead_time_days !== undefined) params.set("lead_time_days", String(query.lead_time_days));
      if (query?.safety_stock_days !== undefined) params.set("safety_stock_days", String(query.safety_stock_days));
      const qs = params.toString();
      return requestJson<{
        data: {
          at_glance: {
            critical: string | null;
            growth: string | null;
            upsell: string | null;
            revenue: string | null;
          };
        };
      }>(`/api/analytics/summary${qs ? `?${qs}` : ""}`, { method: "GET", token });
    },
  },

  reports: {
    async getSalesSummary({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
        product_id: string | number | null;
        category_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));
      if (filters?.product_id !== undefined && filters?.product_id !== null)
        params.set("product_id", String(filters.product_id));
      if (filters?.category_id !== undefined && filters?.category_id !== null)
        params.set("category_id", String(filters.category_id));

      const qs = params.toString();
      const path = `/api/reports/sales-summary${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getSalesTrend({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
        product_id: string | number | null;
        category_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));
      if (filters?.product_id !== undefined && filters?.product_id !== null)
        params.set("product_id", String(filters.product_id));
      if (filters?.category_id !== undefined && filters?.category_id !== null)
        params.set("category_id", String(filters.category_id));

      const qs = params.toString();
      const path = `/api/reports/sales-trend${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getProductPerformance({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
        product_id: string | number | null;
        category_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));
      if (filters?.product_id !== undefined && filters?.product_id !== null)
        params.set("product_id", String(filters.product_id));
      if (filters?.category_id !== undefined && filters?.category_id !== null)
        params.set("category_id", String(filters.category_id));

      const qs = params.toString();
      const path = `/api/reports/product-performance${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getInventorySummary({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
        product_id: string | number | null;
        category_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));
      if (filters?.product_id !== undefined && filters?.product_id !== null)
        params.set("product_id", String(filters.product_id));
      if (filters?.category_id !== undefined && filters?.category_id !== null)
        params.set("category_id", String(filters.category_id));

      const qs = params.toString();
      const path = `/api/reports/inventory-summary${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getRecentOrders({
      token,
      limit,
      filters,
    }: {
      token: string;
      limit?: number;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
        product_id: string | number | null;
        category_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (typeof limit === "number") params.set("limit", String(limit));
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));
      if (filters?.product_id !== undefined && filters?.product_id !== null)
        params.set("product_id", String(filters.product_id));
      if (filters?.category_id !== undefined && filters?.category_id !== null)
        params.set("category_id", String(filters.category_id));

      const qs = params.toString();
      const path = `/api/reports/recent-orders${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },
  },
  adminReports: {
    async getPlatformOverview({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));

      const qs = params.toString();
      const path = `/api/admin/reports/platform-overview${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getBusinessPerformance({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));

      const qs = params.toString();
      const path = `/api/admin/reports/business-performance${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getRecentOrders({
      token,
      limit,
      filters,
    }: {
      token: string;
      limit?: number;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (typeof limit === "number") params.set("limit", String(limit));
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));

      const qs = params.toString();
      const path = `/api/admin/reports/recent-orders${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },

    async getSalesTrend({
      token,
      filters,
    }: {
      token: string;
      filters?: Partial<{
        from_date: string | null;
        to_date: string | null;
        business_id: string | number | null;
      }>;
    }) {
      const params = new URLSearchParams();
      if (filters?.from_date) params.set("from_date", filters.from_date);
      if (filters?.to_date) params.set("to_date", filters.to_date);
      if (filters?.business_id !== undefined && filters?.business_id !== null)
        params.set("business_id", String(filters.business_id));

      const qs = params.toString();
      const path = `/api/admin/reports/sales-trend${qs ? `?${qs}` : ""}`;
      return requestJson<{ data: unknown; meta: unknown }>(path, { method: "GET", token });
    },
  },
} as const;
